import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import TimerWidget from "@/components/TimerWidget";
import JornadaQuickAccess from "@/components/JornadaQuickAccess";
import NotificationBell from "@/components/NotificationBell";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LogOut, Menu, Moon, Sun } from "lucide-react";
import { cn } from "@/lib/utils";

const ROLE_COLORS = {
  "Administrador": "bg-primary/12 text-primary",
  "Directora": "bg-chart-4/12 text-chart-4",
  "Administración": "bg-gold/15 text-[#a9741c] dark:text-gold",
  "Cuentas": "bg-chart-4/12 text-chart-4",
  "Diseñador": "bg-muted text-muted-foreground",
};

export default function Topbar({ onMenu, title }) {
  const { user, logout } = useAuth();
  const [dark, setDark] = useState(() => document.documentElement.classList.contains("dark"));

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
  }, [dark]);

  const initials = (user?.name || "?").split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();

  return (
    <header className="h-16 shrink-0 border-b border-border bg-background/80 backdrop-blur-xl sticky top-0 z-30 flex items-center justify-between px-4 sm:px-6">
      <div className="flex items-center gap-3 min-w-0">
        <Button variant="ghost" size="icon" className="lg:hidden" onClick={onMenu} data-testid="mobile-menu-btn">
          <Menu className="h-5 w-5" />
        </Button>
        <h1 className="font-heading font-bold text-lg tracking-tight truncate">{title}</h1>
      </div>

      <div className="flex items-center gap-2 sm:gap-3">
        <JornadaQuickAccess />
        <TimerWidget />
        <NotificationBell />
        <Button variant="ghost" size="icon" onClick={() => setDark((d) => !d)} className="text-muted-foreground" data-testid="theme-toggle-btn">
          {dark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="user-menu-btn" className="flex items-center gap-2.5 pl-1.5 pr-2 py-1 rounded-md hover:bg-accent transition-colors duration-200">
              <Avatar className="h-8 w-8">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback className="text-xs bg-primary/10 text-primary">{initials}</AvatarFallback>
              </Avatar>
              <div className="hidden sm:flex flex-col items-start leading-none">
                <span className="text-sm font-semibold">{user?.name}</span>
                <span className="text-[11px] text-muted-foreground">{user?.role}</span>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="flex flex-col gap-1">
                <span>{user?.name}</span>
                <span className="text-xs font-normal text-muted-foreground">{user?.email}</span>
                <Badge variant="outline" className={cn("w-fit mt-1 text-[10px] border-transparent", ROLE_COLORS[user?.role])}>{user?.role}</Badge>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid="logout-btn" onClick={logout} className="text-destructive focus:text-destructive">
              <LogOut className="h-4 w-4 mr-2" /> Cerrar sesión
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
